import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  LinearProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WifiOffIcon from "@mui/icons-material/WifiOff";

import { addTenant, checkTenantRegistration } from "../../services/tenantService";

const API_BASE =
  process.env.REACT_APP_SEAM_SERVER_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:8000";

const EMAIL_SERVER =
  process.env.REACT_APP_EMAIL_SERVER_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:3001"
    : "https://ai-realtor.onrender.com");

// ── helpers ──────────────────────────────────────────────────────────────────

const isIgloohomeLock = (lock) => {
  if (!lock) return false;
  const type = (lock.deviceType || "").toLowerCase();
  const manufacturer = (lock.manufacturer || "").toLowerCase();
  const name = (lock.deviceName || "").toLowerCase();
  return (
    type.includes("igloohome") ||
    type.includes("igloo") ||
    manufacturer.includes("igloo") ||
    name.includes("igloo")
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AddTenantDialog = ({
  open,
  onClose,
  listingId,
  propertyAddress,
  availableLocks = [],
  onTenantAdded,
}) => {
  const [form, setForm] = useState({
    email: "",
    leaseStartDate: "",
    leaseEndDate: "",
    lockId: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState("");
  const [error, setError] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const debounceRef = useRef(null);

  // Auto-select lock when only one is available
  useEffect(() => {
    if (availableLocks.length === 1) {
      setForm((prev) => ({ ...prev, lockId: availableLocks[0].lockId }));
    }
  }, [availableLocks]);

  const selectedLock = availableLocks.find((l) => l.lockId === form.lockId);
  const selectedLockIsIgloo = isIgloohomeLock(selectedLock);

  // ── email check on input ───────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "email") {
      setRegistrationStatus(null);
      clearTimeout(debounceRef.current);
      if (value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setRegistrationStatus("checking");
        debounceRef.current = setTimeout(async () => {
          try {
            const { isRegistered } = await checkTenantRegistration(value);
            setRegistrationStatus(isRegistered ? "registered" : "unregistered");
          } catch {
            setRegistrationStatus(null);
          }
        }, 600);
      }
    }
  };

  // ── close ──────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (submitting) return;
    setForm({
      email: "",
      leaseStartDate: "",
      leaseEndDate: "",
      lockId: availableLocks.length === 1 ? availableLocks[0].lockId : "",
    });
    setError("");
    setSubmitStep("");
    setRegistrationStatus(null);
    onClose();
  }, [submitting, availableLocks, onClose]);

  // ── core submit ────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setError("");
    setSubmitStep("");

    if (!form.email || !form.leaseStartDate || !form.leaseEndDate) {
      setError("Please fill in all required fields.");
      return;
    }
    if (new Date(form.leaseEndDate) <= new Date(form.leaseStartDate)) {
      setError("Lease end date must be after the start date.");
      return;
    }
    if (!form.lockId) {
      setError("Please select a lock for this tenant.");
      return;
    }

    setSubmitting(true);

    try {
      // ── 1. Write tenant to Firestore ───────────────────────────────────────
      setSubmitStep("Adding tenant…");
      const newTenant = await addTenant(listingId, {
        email: form.email,
        leaseStartDate: form.leaseStartDate,
        leaseEndDate: form.leaseEndDate,
        lockId: form.lockId,
        status: true,
      });

      if (newTenant.isRegistered) {
        // ── REGISTERED PATH ──────────────────────────────────────────────────
        const isOffline = selectedLockIsIgloo;
        const startsAt = new Date().toISOString();
        const endsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        setSubmitStep(
          isOffline
            ? "Creating offline algoPIN… may take up to 30s"
            : "Creating access code…"
        );

        let generatedCode = null;

        const codeRes = await fetch(`${API_BASE}/api/access-codes/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: "system",
            deviceId: form.lockId,
            name: `Tenant-${listingId}-${form.email.split("@")[0]}-${new Date().toISOString().slice(0, 10)}`,
            startsAt,
            endsAt,
            isOneTimeUse: false,
            isOfflineAccessCode: isOffline,
            tenantId: form.email,
            propertyId: listingId,
            isPermanentTenantCode: true,
          }),
        });

        if (!codeRes.ok) {
          const errBody = await codeRes.json().catch(() => ({}));
          throw new Error(errBody.error || `Code creation failed: HTTP ${codeRes.status}`);
        }

        const codeData = await codeRes.json();
        generatedCode =
          codeData.accessCode?.access_code?.code ||
          codeData.accessCode?.code ||
          codeData.code;

        // Poll for igloohome algoPIN if not immediately available
        if (!generatedCode && isOffline) {
          const seamCodeId =
            codeData.accessCode?.access_code?.access_code_id ||
            codeData.accessCode?.access_code_id;

          if (seamCodeId) {
            for (let attempt = 1; attempt <= 8; attempt++) {
              setSubmitStep(`Waiting for algoPIN… attempt ${attempt}/8`);
              await new Promise((r) => setTimeout(r, 3000));

              const pollRes = await fetch(
                `${API_BASE}/api/access-codes?uid=system&deviceId=${form.lockId}`
              );
              if (pollRes.ok) {
                const pollData = await pollRes.json();
                const found = (pollData.accessCodes?.access_codes || []).find(
                  (c) => c.access_code_id === seamCodeId
                );
                if (found?.code) {
                  generatedCode = found.code;
                  break;
                }
              }
            }
          }
        }

        if (!generatedCode) {
          throw new Error(
            isOffline
              ? "algoPIN was not ready after retries."
              : "No access code returned from server."
          );
        }

        setSubmitStep("Sending access code email…");
        const emailRes = await fetch(
          `${EMAIL_SERVER}/api/tenant-emails/permanent-tenant-code`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tenantEmail: form.email,
              tenantName: form.email,
              accessCode: generatedCode,
              propertyAddress,
              propertyId: listingId,
              lockId: form.lockId,
              isOfflineCode: isOffline,
              isPermanentCode: true,
              isRegistered: true,
              startsAt,
              endsAt,
            }),
          }
        );

        if (!emailRes.ok) {
          const errBody = await emailRes.json().catch(() => ({}));
          console.warn("[AddTenantDialog] Access code email failed:", errBody.error);
        }
      } else {
        // ── UNREGISTERED PATH ────────────────────────────────────────────────
        const isOffline = selectedLockIsIgloo;
        const startsAt = new Date().toISOString();
        const endsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        setSubmitStep(
          isOffline
            ? "Creating offline algoPIN… may take up to 30s"
            : "Creating access code…"
        );

        let generatedCode = null;

        const codeRes = await fetch(`${API_BASE}/api/access-codes/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: "system",
            deviceId: form.lockId,
            name: `Tenant-${listingId}-${form.email.split("@")[0]}-${new Date().toISOString().slice(0, 10)}`,
            startsAt,
            endsAt,
            isOneTimeUse: false,
            isOfflineAccessCode: isOffline,
            tenantId: form.email,
            propertyId: listingId,
            isPermanentTenantCode: true,
          }),
        });

        if (!codeRes.ok) {
          const errBody = await codeRes.json().catch(() => ({}));
          throw new Error(errBody.error || `Code creation failed: HTTP ${codeRes.status}`);
        }

        const codeData = await codeRes.json();
        generatedCode =
          codeData.accessCode?.access_code?.code ||
          codeData.accessCode?.code ||
          codeData.code;

        // Poll for igloohome algoPIN if not immediately available
        if (!generatedCode && isOffline) {
          const seamCodeId =
            codeData.accessCode?.access_code?.access_code_id ||
            codeData.accessCode?.access_code_id;

          if (seamCodeId) {
            for (let attempt = 1; attempt <= 8; attempt++) {
              setSubmitStep(`Waiting for algoPIN… attempt ${attempt}/8`);
              await new Promise((r) => setTimeout(r, 3000));

              const pollRes = await fetch(
                `${API_BASE}/api/access-codes?uid=system&deviceId=${form.lockId}`
              );
              if (pollRes.ok) {
                const pollData = await pollRes.json();
                const found = (pollData.accessCodes?.access_codes || []).find(
                  (c) => c.access_code_id === seamCodeId
                );
                if (found?.code) {
                  generatedCode = found.code;
                  break;
                }
              }
            }
          }
        }

        if (!generatedCode) {
          throw new Error(
            isOffline
              ? "algoPIN was not ready after retries."
              : "No access code returned from server."
          );
        }

        setSubmitStep("Sending invitation email…");

        const emailRes = await fetch(
          `${EMAIL_SERVER}/api/tenant-emails/tenant-onboarding-invite`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tenantEmail: form.email,
              tenantName: form.email,
              propertyAddress,
              propertyId: listingId,
              lockId: form.lockId,
              leaseStartDate: form.leaseStartDate,
              leaseEndDate: form.leaseEndDate,
              accessCode: generatedCode,
              isOfflineCode: isOffline,
              startsAt,
              endsAt,
            }),
          }
        );

        if (!emailRes.ok) {
          const errBody = await emailRes.json().catch(() => ({}));
          console.warn("[AddTenantDialog] Invite email failed (non-fatal):", errBody.error);
        }
      }

      onTenantAdded?.();
    } catch (err) {
      console.error("[AddTenantDialog] submit error:", err);
      setError(err.message || "Failed to add tenant. Please try again.");
    } finally {
      setSubmitting(false);
      setSubmitStep("");
    }
  }, [form, listingId, propertyAddress, selectedLockIsIgloo, onTenantAdded]);

  // ── Registration banner ────────────────────────────────────────────────────
  const renderRegistrationBanner = () => {
    if (registrationStatus === "checking") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Checking account…</Typography>
        </Box>
      );
    }
    if (registrationStatus === "registered") {
      return (
        <Alert severity="success" icon={<PersonIcon fontSize="small" />} sx={{ mt: 1, py: 0.5 }}>
          <strong>Registered.</strong> An access code will be created and emailed to them now.
        </Alert>
      );
    }
    if (registrationStatus === "unregistered") {
      return (
        <Alert severity="info" icon={<EmailIcon fontSize="small" />} sx={{ mt: 1, py: 0.5 }}>
          <strong>No account yet.</strong> They'll get a sign-up invite now. Once they register, their access code will be sent automatically.
        </Alert>
      );
    }
    return null;
  };

  const showSummaryChip =
    registrationStatus &&
    registrationStatus !== "checking" &&
    form.leaseStartDate &&
    form.leaseEndDate &&
    form.lockId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PersonAddIcon color="primary" />
        Add Tenant
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>

          {/* Email */}
          <Box>
            <TextField
              label="Tenant Email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
              placeholder="tenant@example.com"
              disabled={submitting}
            />
            {renderRegistrationBanner()}
          </Box>

          {/* Lease dates */}
          <TextField
            label="Lease Start Date"
            type="date"
            value={form.leaseStartDate}
            onChange={(e) => handleChange("leaseStartDate", e.target.value)}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: form.leaseEndDate || undefined }}
            disabled={submitting}
          />
          <TextField
            label="Lease End Date"
            type="date"
            value={form.leaseEndDate}
            onChange={(e) => handleChange("leaseEndDate", e.target.value)}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: form.leaseStartDate || undefined }}
            disabled={submitting}
          />

          <Divider />

          {/* Lock selector */}
          {availableLocks.length === 0 ? (
            <Alert severity="warning" icon={<VpnKeyIcon fontSize="small" />}>
              No smart locks on this property yet. Add a lock first, then add tenants.
            </Alert>
          ) : (
            <FormControl fullWidth size="small" required>
              <InputLabel>Select Lock</InputLabel>
              <Select
                value={form.lockId}
                onChange={(e) => handleChange("lockId", e.target.value)}
                label="Select Lock"
                disabled={submitting}
              >
                {availableLocks.map((lock) => (
                  <MenuItem key={lock.lockId} value={lock.lockId}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                      <span>
                        {lock.deviceName || "Unnamed Lock"}
                        {lock.manufacturer ? ` (${lock.manufacturer})` : ""}
                      </span>
                      {isIgloohomeLock(lock) && (
                        <Chip
                          icon={<WifiOffIcon sx={{ fontSize: 11 }} />}
                          label="Offline algoPIN"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: "auto", fontSize: 10 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* igloohome notice */}
          {selectedLockIsIgloo && (
            <Alert severity="info" icon={<WifiOffIcon fontSize="small" />} sx={{ py: 0.5 }}>
              <strong>Offline lock.</strong> algoPIN generation can take up to 30s — please keep this dialog open.
            </Alert>
          )}

          {/* Property (read-only) */}
          {propertyAddress && (
            <TextField
              label="Property"
              value={propertyAddress}
              fullWidth
              size="small"
              disabled
            />
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Summary chip */}
          {showSummaryChip && (
            <Chip
              size="small"
              color={registrationStatus === "registered" ? "success" : "info"}
              label={
                registrationStatus === "registered"
                  ? "Access code will be created & emailed immediately"
                  : "Sign-up invite will be sent — code sent after they register"
              }
              sx={{ alignSelf: "flex-start", fontSize: 11 }}
            />
          )}

          {/* Progress */}
          {submitting && (
            <Box>
              <LinearProgress sx={{ mb: 0.5 }} />
              {submitStep && (
                <Typography variant="caption" color="text.secondary">
                  {submitStep}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            !form.email ||
            !form.leaseStartDate ||
            !form.leaseEndDate ||
            !form.lockId ||
            availableLocks.length === 0
          }
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PersonAddIcon />
            )
          }
        >
          {submitting ? submitStep || "Adding…" : "Add Tenant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTenantDialog;